import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface TableState {
    filters?: any[];
    selectedViewId?: string | null;
    selection?: any[];
    sort?: {
        active: string;
        direction: 'asc' | 'desc' | '';
    } | null;
    pageIndex?: number;
    pageSize?: number;
}

@Injectable({
    providedIn: 'root',
})
export class TableStateService {
    // In-memory store: Map<key, TableState>
    // Key format: `table_state_${tabId}_${entite}`
    private stateStore = new Map<string, TableState>();

    // Observable để track state changes
    private stateSubject = new BehaviorSubject<Map<string, TableState>>(new Map());
    public state$: Observable<Map<string, TableState>> = this.stateSubject.asObservable();

    constructor() {
        // Load persisted states from sessionStorage on init
        // Đảm bảo state được restore sau khi refresh page
        this.loadPersistedStates();
    }

    /**
     * Get state for a specific tab
     * Priority: In-memory store (fastest) → sessionStorage (persistence)
     */
    getState(tabId: string | null, entite: string): TableState | null {
        if (!tabId) {
            return null;
        }

        const key = this.getStateKey(tabId, entite);

        // 1. Try in-memory store first (fastest, no race conditions)
        const state = this.stateStore.get(key);

        if (state) {
            return state;
        }

        // 2. Fallback to sessionStorage (for page refresh scenario)
        try {
            const stateStr = sessionStorage.getItem(key);
            if (stateStr) {
                const parsedState = JSON.parse(stateStr) as TableState;
                // Load into in-memory store for next time
                this.stateStore.set(key, parsedState);
                return parsedState;
            }
        } catch (error) {
            console.error(`Error loading state from sessionStorage for ${key}:`, error);
        }

        return null;
    }

    /**
     * Save state for a specific tab
     * Saves to BOTH in-memory store (immediate) and sessionStorage (persistence)
     */
    saveState(tabId: string | null, entite: string, state: TableState): void {
        if (!tabId) {
            return;
        }

        const key = this.getStateKey(tabId, entite);

        // 1. Save to in-memory store (immediate, synchronous, no race conditions)
        this.stateStore.set(key, { ...state });
        this.stateSubject.next(new Map(this.stateStore));

        // 2. Persist to sessionStorage (for page refresh scenario)
        this.persistState(key, state);
    }

    /**
     * Clear state for a specific tab
     */
    clearState(tabId: string | null, entite: string): void {
        if (!tabId) {
            return;
        }

        const key = this.getStateKey(tabId, entite);

        // Clear from in-memory store
        this.stateStore.delete(key);
        this.stateSubject.next(new Map(this.stateStore));

        // Clear from sessionStorage
        try {
            sessionStorage.removeItem(key);
        } catch (error) {
            console.error('Error clearing state from sessionStorage:', error);
        }
    }

    /**
     * Clear all states for a specific entity
     * Useful when closing a tab - ensures that when the tab is reopened, it uses default visualization
     */
    clearStatesForEntity(entite: string): void {
        const suffix = `_${entite}`;
        const keysToRemove: string[] = [];

        // 1. Find all keys for this entity in in-memory store
        for (const [key] of this.stateStore.entries()) {
            if (key.endsWith(suffix)) {
                keysToRemove.push(key);
            }
        }

        // 2. Remove from in-memory store
        keysToRemove.forEach((key) => {
            this.stateStore.delete(key);
        });
        this.stateSubject.next(new Map(this.stateStore));

        // 3. Remove from sessionStorage
        try {
            const sessionKeysToRemove: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.endsWith(suffix)) {
                    sessionKeysToRemove.push(key);
                }
            }
            sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));
        } catch (error) {
            console.error(`Error clearing states for entity ${entite}:`, error);
        }
    }

    /**
     * Clear all states (useful when logging out)
     */
    clearAllStates(): void {
        // Clear in-memory store
        this.stateStore.clear();
        this.stateSubject.next(new Map());

        // Clear all persisted states
        try {
            const keysToRemove: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('table_state_')) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach((key) => sessionStorage.removeItem(key));
        } catch (error) {
            console.error('Error clearing all states:', error);
        }
    }

    /**
     * Get state key from tabId and entite
     */
    private getStateKey(tabId: string, entite: string): string {
        return `table_state_${tabId}_${entite}`;
    }

    /**
     * Get state for a specific entity (fallback when tabId doesn't match)
     * Returns the most recent state for the given entity
     */
    getStateForEntity(entite: string, excludeTabId?: string | null): TableState | null {
        const prefix = `table_state_`;
        const suffix = `_${entite}`;
        let latestState: TableState | null = null;
        let latestKey: string | null = null;

        // 1. Try in-memory store first
        for (const [key, state] of this.stateStore.entries()) {
            if (key.startsWith(prefix) && key.endsWith(suffix)) {
                if (excludeTabId && key.includes(`_${excludeTabId}_`)) {
                    continue; // Skip excluded tabId
                }
                // Use the first matching state (or could implement timestamp-based selection)
                if (!latestState) {
                    latestState = state;
                    latestKey = key;
                }
            }
        }

        if (latestState) {
            return latestState;
        }

        // 2. Fallback to sessionStorage
        try {
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith(prefix) && key.endsWith(suffix)) {
                    if (excludeTabId && key.includes(`_${excludeTabId}_`)) {
                        continue; // Skip excluded tabId
                    }
                    const stateStr = sessionStorage.getItem(key);
                    if (stateStr) {
                        const parsedState = JSON.parse(stateStr) as TableState;
                        // Load into in-memory store for next time
                        this.stateStore.set(key, parsedState);
                        if (!latestState) {
                            latestState = parsedState;
                            latestKey = key;
                        }
                    }
                }
            }
        } catch (error) {
            console.error(`Error loading state from sessionStorage for entity ${entite}:`, error);
        }

        return latestState;
    }

    /**
     * Persist state to sessionStorage (non-blocking, optional)
     * This allows state to survive page refresh
     */
    private persistState(key: string, state: TableState): void {
        try {
            sessionStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
            // Handle QuotaExceededError gracefully
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                console.warn('sessionStorage quota exceeded, clearing old states...');
                this.clearOldPersistedStates();
                // Retry once
                try {
                    sessionStorage.setItem(key, JSON.stringify(state));
                } catch (retryError) {
                    console.error('Failed to persist state after clearing:', retryError);
                    // Even if persistence fails, in-memory store still works
                }
            } else {
                console.error('Error persisting state:', error);
            }
        }
    }

    /**
     * Load persisted states from sessionStorage on init
     * Đảm bảo state được restore sau khi refresh page
     */
    private loadPersistedStates(): void {
        try {
            const keysToLoad: string[] = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('table_state_')) {
                    keysToLoad.push(key);
                }
            }

            keysToLoad.forEach((key) => {
                try {
                    const stateStr = sessionStorage.getItem(key);
                    if (stateStr) {
                        const state = JSON.parse(stateStr);
                        // Load into in-memory store
                        this.stateStore.set(key, state);
                    }
                } catch (error) {
                    console.error(`Error loading state for key ${key}:`, error);
                    // Remove corrupted state
                    try {
                        sessionStorage.removeItem(key);
                    } catch (removeError) {
                        console.error('Error removing corrupted state:', removeError);
                    }
                }
            });

            if (this.stateStore.size > 0) {
                this.stateSubject.next(new Map(this.stateStore));
            }
        } catch (error) {
            console.error('Error loading persisted states:', error);
        }
    }

    /**
     * Clear old persisted states to free space
     * Keeps only the most recent states (last 20 tabs)
     */
    private clearOldPersistedStates(): void {
        try {
            const states: Array<{ key: string; timestamp: number }> = [];

            // Collect all table states
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('table_state_')) {
                    states.push({
                        key: key,
                        timestamp: Date.now(), // Could be improved with actual timestamp from state
                    });
                }
            }

            // Keep only last 20 states
            if (states.length > 20) {
                const statesToRemove = states.slice(0, states.length - 20);
                statesToRemove.forEach(({ key }) => {
                    sessionStorage.removeItem(key);
                    // Also remove from in-memory store
                    this.stateStore.delete(key);
                });
                this.stateSubject.next(new Map(this.stateStore));
            }
        } catch (error) {
            console.error('Error clearing old persisted states:', error);
        }
    }
}
